export type TicketStatus = 'abierto' | 'proceso' | 'resuelto' | 'cerrado';
export type TicketPriority = 'baja' | 'normal' | 'alta' | 'urgente';

export interface TicketReply {
  id: number;
  author: string;
  authorRole: 'admin' | 'soporte' | 'staff';
  message: string;
  createdAt: string;
}

export interface Ticket {
  id: number;
  subject: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  requester: string;
  requesterEmail: string;
  assignedTo: string;
  createdAt: string;
  updatedAt: string;
  replies: TicketReply[];
}

export const statusMeta: Record<TicketStatus, { label: string; color: string; bgColor: string; icon: string; dot: string }> = {
  abierto: {
    label: 'Abierto',
    color: 'text-sky-700',
    bgColor: 'bg-sky-100',
    icon: 'ri-mail-open-line',
    dot: 'bg-sky-500',
  },
  proceso: {
    label: 'En Proceso',
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    icon: 'ri-loader-4-line',
    dot: 'bg-amber-500',
  },
  resuelto: {
    label: 'Resuelto',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-100',
    icon: 'ri-check-double-line',
    dot: 'bg-emerald-500',
  },
  cerrado: {
    label: 'Cerrado',
    color: 'text-foreground-600',
    bgColor: 'bg-background-200',
    icon: 'ri-lock-line',
    dot: 'bg-foreground-400',
  },
};

export const priorityMeta: Record<TicketPriority, { label: string; color: string; bgColor: string; dot: string }> = {
  baja: {
    label: 'Baja',
    color: 'text-foreground-500',
    bgColor: 'bg-background-200',
    dot: 'bg-foreground-400',
  },
  normal: {
    label: 'Normal',
    color: 'text-sky-700',
    bgColor: 'bg-sky-100',
    dot: 'bg-sky-500',
  },
  alta: {
    label: 'Alta',
    color: 'text-amber-700',
    bgColor: 'bg-amber-100',
    dot: 'bg-amber-500',
  },
  urgente: {
    label: 'Urgente',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    dot: 'bg-red-500',
  },
};

export const allStatuses: TicketStatus[] = ['abierto', 'proceso', 'resuelto', 'cerrado'];
export const allPriorities: TicketPriority[] = ['baja', 'normal', 'alta', 'urgente'];

export const supportStaff = ['Carlos Mendoza', 'Laura Fuentes', 'Diego Alvarado'];

export const supportTickets: Ticket[] = [
  {
    id: 1001,
    subject: 'Stream se cae cada 30 minutos',
    description: 'Desde ayer la transmisión se está cortando cada media hora aproximadamente. Ya reinicié el encoder y el servidor pero el problema persiste. Los oyentes reportan que se va el audio de golpe y vuelve a los 2-3 minutos.',
    status: 'abierto',
    priority: 'urgente',
    requester: 'Andrea Ramírez',
    requesterEmail: 'andrea@radiofrecuencia.com',
    assignedTo: 'Carlos Mendoza',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
    replies: [
      {
        id: 1,
        author: 'Carlos Mendoza',
        authorRole: 'soporte',
        message: 'Hola Andrea, revisé los logs del servidor y veo picos de CPU justo antes de cada corte. ¿Podés confirmarme qué encoder están usando y la versión? Mientras tanto voy a escalar al equipo de infra.',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 1).toISOString(),
      },
    ],
  },
  {
    id: 1002,
    subject: 'No puedo subir fotos a la galería',
    description: 'Estoy intentando subir fotos del evento de anoche pero me sale error 403 cada vez que intento. Ya probé con diferentes navegadores y formatos de imagen. Las fotos pesan entre 2 y 5 MB.',
    status: 'proceso',
    priority: 'alta',
    requester: 'José Morales',
    requesterEmail: 'jose@radiofrecuencia.com',
    assignedTo: 'Laura Fuentes',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
    replies: [
      {
        id: 2,
        author: 'Laura Fuentes',
        authorRole: 'soporte',
        message: 'José, revisé tu cuenta y detecté que tu plan Free tiene el storage lleno al 98%. El error 403 es porque no tenés espacio disponible. Te recomiendo eliminar algunas fotos viejas o considerar el upgrade a Pro.',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
      },
      {
        id: 3,
        author: 'José Morales',
        authorRole: 'staff',
        message: 'Ahh con razón. Voy a borrar las fotos del 2024 y vuelvo a intentar. Gracias Laura.',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
      },
    ],
  },
  {
    id: 1003,
    subject: 'Solicitud de frecuencia personalizada',
    description: 'Quisiera solicitar una frecuencia personalizada para mi radio. Actualmente estamos en automática pero nos gustaría tener una frecuencia fija que los oyentes puedan recordar fácilmente. Idealmente algo como 98.7 FM.',
    status: 'proceso',
    priority: 'normal',
    requester: 'Fernando Paz',
    requesterEmail: 'fernando@radiofrecuencia.com',
    assignedTo: 'Diego Alvarado',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
    replies: [
      {
        id: 4,
        author: 'Diego Alvarado',
        authorRole: 'soporte',
        message: 'Hola Fernando, las frecuencias personalizadas están disponibles solo en plan Premium. Actualmente tenés Pro. ¿Te interesaría evaluar el upgrade? Puedo enviarte la comparativa de beneficios.',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 8).toISOString(),
      },
    ],
  },
  {
    id: 1004,
    subject: 'Problema con el chat unificado',
    description: 'El chat unificado no está mostrando los mensajes de YouTube. Los de Facebook y WhatsApp sí aparecen, pero YouTube no sincroniza desde ayer a las 3pm.',
    status: 'abierto',
    priority: 'alta',
    requester: 'Valeria Castillo',
    requesterEmail: 'valeria@radiofrecuencia.com',
    assignedTo: 'Carlos Mendoza',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 28).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 28).toISOString(),
    replies: [],
  },
  {
    id: 1005,
    subject: 'Consulto sobre analytics avanzados',
    description: 'Hola, quería saber qué incluyen exactamente los analytics avanzados del plan Premium. Me interesa especialmente el tema de retención de oyentes y mapas de calor de audiencia.',
    status: 'resuelto',
    priority: 'baja',
    requester: 'Diego Montenegro',
    requesterEmail: 'diego@radiofrecuencia.com',
    assignedTo: 'Laura Fuentes',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 72).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
    replies: [
      {
        id: 5,
        author: 'Laura Fuentes',
        authorRole: 'soporte',
        message: 'Hola Diego, los analytics avanzados incluyen: retención de oyentes por franja horaria, mapas de calor geográficos, dispositivos de escucha, duración media de sesión, y reportes exportables. Todo está disponible en Premium. ¿Querés que te active una demo de 7 días?',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
      },
      {
        id: 6,
        author: 'Diego Montenegro',
        authorRole: 'staff',
        message: '¡Genial! Sí, me encantaría probar la demo. Muchas gracias por la info.',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 36).toISOString(),
      },
      {
        id: 7,
        author: 'Laura Fuentes',
        authorRole: 'soporte',
        message: 'Perfecto Diego, ya te activé la demo de 7 días de Premium. La verás reflejada en tu panel en unos minutos. Cualquier duda me avisás.',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
      },
    ],
  },
  {
    id: 1006,
    subject: 'Reporte de abuso en chat — usuario reincidente',
    description: 'El usuario "elbromista777" sigue enviando mensajes ofensivos en el chat a pesar de que ya lo banee dos veces. Parece que crea cuentas nuevas con el mismo patrón de nombre.',
    status: 'proceso',
    priority: 'alta',
    requester: 'Carlos Estrada',
    requesterEmail: 'carlos@radiofrecuencia.com',
    assignedTo: 'Diego Alvarado',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
    replies: [
      {
        id: 8,
        author: 'Diego Alvarado',
        authorRole: 'soporte',
        message: 'Carlos, ya identifiqué el patrón. Implementé un filtro de IP para bloquear el rango desde donde está creando las cuentas. También agregué la palabra clave a la blacklist del chat. Monitoreemos las próximas 24hs.',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
      },
    ],
  },
  {
    id: 1007,
    subject: 'No recibo notificaciones de nuevos seguidores',
    description: 'Desde el lunes dejé de recibir las notificaciones cuando alguien nuevo sigue mi radio. Antes me llegaban todas. Revisé la configuración y parece estar bien.',
    status: 'abierto',
    priority: 'normal',
    requester: 'María López',
    requesterEmail: 'maria@radiofrecuencia.com',
    assignedTo: 'Laura Fuentes',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 16).toISOString(),
    replies: [
      {
        id: 9,
        author: 'Laura Fuentes',
        authorRole: 'soporte',
        message: 'María, revisé tu cuenta y las notificaciones están activas en el sistema. ¿Podrías confirmarme si en el panel de Preferencias de Notificación tenés activado "Nuevo Seguidor"? A veces el toggle se resetea con las actualizaciones.',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 18).toISOString(),
      },
    ],
  },
  {
    id: 1008,
    subject: 'Programar live para el viernes',
    description: 'Necesito ayuda para configurar el live del viernes a las 20:00. Es un evento especial de aniversario y quiero asegurarme de que todo funcione bien. Vamos a transmitir por YouTube y Facebook simultáneamente.',
    status: 'abierto',
    priority: 'normal',
    requester: 'Andrea Ramírez',
    requesterEmail: 'andrea@radiofrecuencia.com',
    assignedTo: 'Carlos Mendoza',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
    replies: [],
  },
  {
    id: 1009,
    subject: 'Error al conectar cuenta de Instagram',
    description: 'Estoy intentando conectar mi cuenta de Instagram en Social Connectors pero me tira error de autenticación. Ya intenté reconectar tres veces. Mi cuenta de Facebook sí se conectó bien.',
    status: 'resuelto',
    priority: 'normal',
    requester: 'Fernando Paz',
    requesterEmail: 'fernando@radiofrecuencia.com',
    assignedTo: 'Diego Alvarado',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 96).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
    replies: [
      {
        id: 10,
        author: 'Diego Alvarado',
        authorRole: 'soporte',
        message: 'Fernando, el problema era que tu cuenta de Instagram tenía activada la verificación en dos pasos con una app que ya no usás. Te desconecté la sesión anterior del lado del servidor. Intentá conectar de nuevo ahora, debería funcionar.',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 56).toISOString(),
      },
      {
        id: 11,
        author: 'Fernando Paz',
        authorRole: 'staff',
        message: '¡Funcionó! Muchas gracias Diego. Cierro el ticket.',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString(),
      },
    ],
  },
  {
    id: 1010,
    subject: 'Plan Free — consulta límite oyentes simultáneos',
    description: 'Tengo el plan Free y quería saber cuál es el límite de oyentes simultáneos. He notado que a veces cuando pasamos de 50 oyentes la calidad del stream baja.',
    status: 'cerrado',
    priority: 'baja',
    requester: 'José Morales',
    requesterEmail: 'jose@radiofrecuencia.com',
    assignedTo: 'Laura Fuentes',
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 120).toISOString(),
    updatedAt: new Date(Date.now() - 1000 * 60 * 60 * 96).toISOString(),
    replies: [
      {
        id: 12,
        author: 'Laura Fuentes',
        authorRole: 'soporte',
        message: 'Hola José, el plan Free soporta hasta 100 oyentes simultáneos con calidad estándar (128kbps). Si notás bajones de calidad con 50+ oyentes puede ser un tema de ancho de banda de tu conexión, no del servidor. ¿Podrías hacer un speed test cuando ocurra?',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 110).toISOString(),
      },
      {
        id: 13,
        author: 'José Morales',
        authorRole: 'staff',
        message: 'Tenés razón, hice el speed test y mi subida estaba saturada. Ya lo resolví con mi proveedor. ¡Gracias!',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 100).toISOString(),
      },
      {
        id: 14,
        author: 'Laura Fuentes',
        authorRole: 'soporte',
        message: '¡Excelente José! Me alegro que lo hayas resuelto. Cierro el ticket. Cualquier cosa estamos a disposición.',
        createdAt: new Date(Date.now() - 1000 * 60 * 60 * 96).toISOString(),
      },
    ],
  },
];